// src/contexts/StepUpContext.jsx — The "vault" step-up gate.
//
// Wrap a sensitive reveal in `await requireStepUp()`. If the user verified an
// authenticator code within the last 15 minutes, it resolves immediately;
// otherwise a modal asks for a fresh 6-digit code. Resolves true once elevated,
// false if the user cancels (or has no authenticator enrolled yet).
//
// This is the app-layer gate (UX + reveal control). The un-bypassable database
// enforcement (RLS on AAL2) lands in slice 2, once the whole team has enrolled.

import { createContext, useContext, useRef, useState, useCallback } from 'react'
import { Shield, X, Loader2 } from 'lucide-react'
import { hasVerifiedFactor, verifyStepUp } from '../lib/mfa'

const WINDOW_MS = 15 * 60 * 1000   // a verification is good for 15 minutes
const KEY = 'chatrone_stepup_until'

const StepUpContext = createContext(null)

function isElevated() {
  const until = Number(sessionStorage.getItem(KEY) || 0)
  return until > Date.now()
}
function markElevated() {
  sessionStorage.setItem(KEY, String(Date.now() + WINDOW_MS))
}

export function StepUpProvider({ children }) {
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState('code')   // 'code' | 'needs_enroll'
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const resolverRef = useRef(null)

  // Returns Promise<boolean> — true once the session is freshly elevated.
  const requireStepUp = useCallback(async () => {
    if (isElevated()) return true
    const enrolled = await hasVerifiedFactor()
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setCode(''); setError(null)
      setPhase(enrolled ? 'code' : 'needs_enroll')
      setOpen(true)
    })
  }, [])

  function finish(result) {
    setOpen(false); setBusy(false); setCode(''); setError(null)
    const r = resolverRef.current; resolverRef.current = null
    if (r) r(result)
  }

  async function submit(e) {
    e?.preventDefault?.()
    if (busy) return
    if (!/^\d{6}$/.test(code.trim())) { setError('Enter the 6-digit code from your authenticator.'); return }
    setBusy(true); setError(null)
    const res = await verifyStepUp(code.trim())
    if (res.ok) { markElevated(); finish(true); return }
    setBusy(false)
    setError(res.error === 'no_factor'
      ? 'No authenticator is set up on your account yet.'
      : 'That code didn’t match. Try the current code from your app.')
  }

  return (
    <StepUpContext.Provider value={{ requireStepUp }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={() => finish(false)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-navy-800">
                <Shield size={18} />
                <h3 className="text-sm font-semibold uppercase tracking-wide font-mono">Verify to view</h3>
              </div>
              <button onClick={() => finish(false)} className="text-warm-400 hover:text-warm-700"><X size={18} /></button>
            </div>

            {phase === 'needs_enroll' ? (
              <div className="space-y-3">
                <p className="text-sm text-warm-700">
                  This is confidential information. Set up your authenticator app first to view it.
                </p>
                <a href="/settings" className="block text-center rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900">
                  Go to Settings → Security
                </a>
                <button onClick={() => finish(false)} className="w-full text-xs text-warm-500 hover:text-warm-700">Cancel</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <p className="text-sm text-warm-700">
                  Enter the current 6-digit code from your authenticator app to view confidential data.
                </p>
                <input
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="input-field w-full text-center text-2xl tracking-[0.5em] font-mono"
                />
                {error && <p className="text-xs text-terracotta-600">{error}</p>}
                <button type="submit" disabled={busy} className="w-full rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={14} className="animate-spin" />} Verify
                </button>
                <p className="text-[11px] text-warm-400 text-center">Stays unlocked for 15 minutes.</p>
              </form>
            )}
          </div>
        </div>
      )}
    </StepUpContext.Provider>
  )
}

export function useStepUp() {
  const ctx = useContext(StepUpContext)
  if (!ctx) throw new Error('useStepUp must be used within a StepUpProvider')
  return ctx
}
